import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMarqueeItem } from '@/lib/api'
import Modal from '@/components/Modal'
import RowActions from '@/components/RowActions'
import { useConfirm } from '@/components/ConfirmDialog'

export default function MarqueePage() {
  const { token } = useAuth()

  const [items, setItems] = useState<AdminMarqueeItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({ date: '', text: '' })
  const [saving, setSaving] = useState(false)
  const { confirm, dialog } = useConfirm()

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      setItems(await api.getAdminMarquee(token))
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      await api.createMarquee(token, form)
      setModalOpen(false)
      setForm({ date: '', text: '' })
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, text: string) => {
    const preview = text.length > 50 ? text.slice(0, 50) + '…' : text
    if (!await confirm(`"${preview}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: 'Hapus Marquee' })) return
    if (!token) return
    try {
      await api.deleteMarquee(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  const sorted = [...items].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Marquee</h1>
          <p className="text-zinc-500 text-xs mt-0.5">{items.length} ticker items</p>
        </div>
        <button
          onClick={() => { setForm({ date: '', text: '' }); setModalOpen(true) }}
          className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
        >
          + Add Item
        </button>
      </div>

      {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">No marquee items yet.</p>
          <button
            onClick={() => { setForm({ date: '', text: '' }); setModalOpen(true) }}
            className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            + Add the first one
          </button>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-zinc-800">
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Date</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Text</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {sorted.map((item) => (
                <tr key={item.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{item.date}</td>
                  <td className="px-4 py-3 text-white">{item.text}</td>
                  <td className="px-4 py-3">
                    <RowActions actions={[
                      { label: 'Hapus', onClick: () => handleDelete(item.id, item.text), variant: 'danger' },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog}
      {modalOpen && (
        <Modal title="New Marquee Item" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                required
                className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500"
              />
            </div>

            <div>
              <label className="block text-xs text-zinc-400 mb-1">Text</label>
              <input
                type="text"
                value={form.text}
                onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))}
                required
                placeholder="Ticker message…"
                className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-sm text-zinc-400 hover:text-white transition-colors px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold px-6 py-2 rounded transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
